<template>
  <TransitionGroup v-if="!!group" v-bind="transitionProps">
    <slot />
  </TransitionGroup>
  <Transition v-else v-bind="transitionProps">
    <slot />
  </Transition>
</template>

<style lang="postcss">
.v-enter-to {
  animation-duration: var(--enter-duration);
  animation-timing-function: var(--easing);
}
.v-leave-to {
  animation-duration: var(--leave-duration);
  animation-timing-function: var(--easing);
}
</style>

<script>
import { computed, onMounted, toRefs, watchEffect } from '@vue/composition-api';
import { getTouchedScreen } from '@/lego/utils/tailwind/screen';
import { defineCssVars } from '@/lego/utils/style/vars';

export default {
  name: 'AnimationComponent',

  props: {
    enter: {
      type: [String, Object],
      required: false,
    },

    leave: {
      type: [String, Object],
      required: false,
    },

    duration: {
      type: [Number, Object],
      default: 300,
    },

    easing: {
      type: String,
      default: 'inherit',
    },

    disable: {
      type: Boolean,
      default: false,
    },

    group: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { attrs, slots }) {
    const { enter, leave, duration, easing, disable } = toRefs(props);

    const enterDuration = computed(
      () =>
        (disable.value ? 0 : duration.value?.enter || duration.value) + 'ms',
    );
    const leaveDuration = computed(
      () =>
        (disable.value ? 0 : duration.value?.leave || duration.value) + 'ms',
    );

    onMounted(() =>
      watchEffect(() =>
        defineCssVars(
          {
            '--enter-duration': enterDuration.value,
            '--leave-duration': leaveDuration.value,
            '--easing': easing.value,
          },
          // Las variables CSS se declaran en el elemento root del slot
          // para no sobreescribir otras variables ya declaradas.
          slots.default()[0]?.elm,
        ),
      ),
    );

    function getAnimation(breakpoints) {
      const screen = getTouchedScreen(breakpoints);

      if (breakpoints[screen]) {
        return breakpoints[screen];
      }
    }

    return {
      transitionProps: computed(() => {
        const enterAnimation =
          !enter.value || typeof enter.value === 'string'
            ? enter.value
            : getAnimation(enter.value);
        const leaveAnimation =
          !leave.value || typeof leave.value === 'string'
            ? leave.value
            : getAnimation(leave.value);

        return {
          enterActiveClass: `animate__animated animate__${enterAnimation}`,
          leaveActiveClass: `animate__animated animate__${leaveAnimation}`,
          ...attrs,
        };
      }),
    };
  },
};
</script>
